import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import type { AgentMission } from '../../api/schema'
import { useLocale } from '../../contexts/LocaleContext'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'

/** The output names the card renders; any other is left to the run page. */
export const RENDERED_OUTPUTS = ['score_before', 'score_after', 'audit', 'reconciliation', 'files_to_request'] as const

const AUDIT_ROWS = 10

interface AuditRow {
    theme: string
    finding: string
    status: 'missing' | 'wrong' | 'invented' | 'ok'
    document_uid?: string
    page?: number
    fixed_by_step?: string
}

interface ReconciliationRow {
    floor_asset_uid: string
    equipment_type: string
    plan_count: number
    report_count: number
    report_document_uid?: string
    page?: number
    gap?: number
    note?: string
}

interface FileToRequest {
    what: string
    from: string
    why: string
    floor_asset_uid?: string
}

const AUDIT_STATUS_VARIANT: Record<AuditRow['status'], 'destructive' | 'warning' | 'success'> = {
    missing: 'warning',
    wrong: 'destructive',
    invented: 'destructive',
    ok: 'success',
}

function asArray<T>(v: unknown): T[] {
    return Array.isArray(v) ? (v as T[]) : []
}

function SourceLink({ org, documentUid, page }: { org: string; documentUid?: string; page?: number }) {
    const { t } = useTranslation('agent-runs')
    if (!documentUid) return <span className="text-muted-foreground">—</span>
    const label = page ? t('outputs.source_page', { page }) : t('outputs.source')
    return (
        <Link
            to="/$org/documents/$docUid"
            params={{ org, docUid: documentUid }}
            search={(page ? { page } : {}) as never}
            className="text-primary hover:underline"
            data-testid="agent-run-output-source"
        >
            {label}
        </Link>
    )
}

function FloorLink({ org, uid, names }: { org: string; uid?: string; names: Record<string, string> }) {
    if (!uid) return <span className="text-muted-foreground">—</span>
    return (
        <Link to="/$org/assets/$assetUid" params={{ org, assetUid: uid }} className="text-primary hover:underline">
            {names[uid] ?? uid.slice(0, 8)}
        </Link>
    )
}

/**
 * The mission's outputs, rendered by output name (spec 2026-10-21-19 §3), so a
 * mission keeping the macro's field names needs no front change.
 */
export function AgentRunOutputs({
    org,
    output,
    mission,
    floorNames = {},
}: {
    org: string
    output: Record<string, unknown> | null | undefined
    mission?: AgentMission
    floorNames?: Record<string, string>
}) {
    const { t } = useTranslation('agent-runs')
    const { locale } = useLocale()
    const [allAudit, setAllAudit] = useState(false)
    if (!output) return null

    const num = (n: number) => n.toLocaleString(locale, { maximumFractionDigits: 1 })
    const stepTitle = (code?: string) => {
        if (!code) return ''
        const step = mission?.steps.find((s) => s.code === code)
        return step ? t(step.title_key, { defaultValue: code }) : code
    }

    const before = typeof output.score_before === 'number' ? output.score_before : undefined
    const after = typeof output.score_after === 'number' ? output.score_after : undefined
    const audit = asArray<AuditRow>(output.audit)
    const reconciliation = [...asArray<ReconciliationRow>(output.reconciliation)].sort(
        (a, b) => Number((b.gap ?? 0) !== 0) - Number((a.gap ?? 0) !== 0),
    )
    const files = asArray<FileToRequest>(output.files_to_request)
    const shownAudit = allAudit ? audit : audit.slice(0, AUDIT_ROWS)

    return (
        <div className="space-y-4">
            {(before !== undefined || after !== undefined) && (
                <div className="text-sm font-medium text-foreground" data-testid="agent-run-output-scores">
                    {t('outputs.scores', {
                        before: before !== undefined ? num(before) : '—',
                        after: after !== undefined ? num(after) : '—',
                    })}
                </div>
            )}

            {audit.length > 0 && (
                <div data-testid="agent-run-output-audit">
                    <h3 className="mb-2 text-sm font-semibold text-foreground">{t('outputs.audit_title')}</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-xs text-muted-foreground">
                                <tr>
                                    <th className="py-1 pr-3 font-medium">{t('outputs.theme')}</th>
                                    <th className="py-1 pr-3 font-medium">{t('outputs.finding')}</th>
                                    <th className="py-1 pr-3 font-medium">{t('outputs.status')}</th>
                                    <th className="py-1 pr-3 font-medium">{t('outputs.source')}</th>
                                    <th className="py-1 font-medium">{t('outputs.fixed_by')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shownAudit.map((row, i) => (
                                    <tr key={i} className="border-t border-border align-top" data-testid={`agent-run-output-audit-row-${i}`}>
                                        <td className="py-1.5 pr-3 whitespace-nowrap">{t(`audit.themes.${row.theme}`, { defaultValue: row.theme })}</td>
                                        <td className="py-1.5 pr-3 min-w-48">{row.finding}</td>
                                        <td className="py-1.5 pr-3">
                                            <Badge variant={AUDIT_STATUS_VARIANT[row.status] ?? 'secondary'}>
                                                {t(`audit.status.${row.status}`, { defaultValue: row.status })}
                                            </Badge>
                                        </td>
                                        <td className="py-1.5 pr-3 whitespace-nowrap">
                                            <SourceLink org={org} documentUid={row.document_uid} page={row.page} />
                                        </td>
                                        <td className="py-1.5 whitespace-nowrap">{stepTitle(row.fixed_by_step)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {audit.length > AUDIT_ROWS && !allAudit && (
                        <Button variant="link" size="sm" className="px-0" onClick={() => setAllAudit(true)} data-testid="agent-run-output-audit-all">
                            {t('outputs.show_all', { count: audit.length })}
                        </Button>
                    )}
                </div>
            )}

            {reconciliation.length > 0 && (
                <div data-testid="agent-run-output-reconciliation">
                    <h3 className="mb-2 text-sm font-semibold text-foreground">{t('outputs.reconciliation_title')}</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-xs text-muted-foreground">
                                <tr>
                                    <th className="py-1 pr-3 font-medium">{t('outputs.floor')}</th>
                                    <th className="py-1 pr-3 font-medium">{t('outputs.equipment_type')}</th>
                                    <th className="py-1 pr-3 font-medium text-right">{t('outputs.plan_count')}</th>
                                    <th className="py-1 pr-3 font-medium text-right">{t('outputs.report_count')}</th>
                                    <th className="py-1 pr-3 font-medium text-right">{t('outputs.gap')}</th>
                                    <th className="py-1 pr-3 font-medium">{t('outputs.source')}</th>
                                    <th className="py-1 font-medium">{t('outputs.note')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reconciliation.map((row, i) => (
                                    <tr key={i} className="border-t border-border align-top" data-testid={`agent-run-output-reconciliation-row-${i}`}>
                                        <td className="py-1.5 pr-3 whitespace-nowrap"><FloorLink org={org} uid={row.floor_asset_uid} names={floorNames} /></td>
                                        <td className="py-1.5 pr-3 whitespace-nowrap">{t(`equipment.${row.equipment_type}`, { defaultValue: row.equipment_type })}</td>
                                        <td className="py-1.5 pr-3 text-right tabular-nums">{row.plan_count}</td>
                                        <td className="py-1.5 pr-3 text-right tabular-nums">{row.report_count}</td>
                                        <td className={`py-1.5 pr-3 text-right tabular-nums ${row.gap ? 'font-semibold text-warning' : ''}`}>{row.gap ?? 0}</td>
                                        <td className="py-1.5 pr-3 whitespace-nowrap">
                                            <SourceLink org={org} documentUid={row.report_document_uid} page={row.page} />
                                        </td>
                                        <td className="py-1.5 min-w-32">{row.note}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {files.length > 0 && (
                <div data-testid="agent-run-output-files">
                    <h3 className="mb-2 text-sm font-semibold text-foreground">{t('outputs.files_title')}</h3>
                    <ul className="space-y-1.5 text-sm">
                        {files.map((f, i) => (
                            <li key={i} data-testid={`agent-run-output-files-row-${i}`}>
                                <span className="font-medium text-foreground">{f.what}</span>
                                <span className="text-muted-foreground"> · {t(`outputs.from.${f.from}`, { defaultValue: f.from })}</span>
                                {f.floor_asset_uid && (
                                    <span className="text-muted-foreground"> · <FloorLink org={org} uid={f.floor_asset_uid} names={floorNames} /></span>
                                )}
                                <div className="text-muted-foreground">{f.why}</div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}

/** Floor uids the outputs name, for one useAssetsByUids call. */
export function outputFloorUids(output: Record<string, unknown> | null | undefined): string[] {
    if (!output) return []
    const uids = new Set<string>()
    for (const r of asArray<ReconciliationRow>(output.reconciliation)) if (r.floor_asset_uid) uids.add(r.floor_asset_uid)
    for (const f of asArray<FileToRequest>(output.files_to_request)) if (f.floor_asset_uid) uids.add(f.floor_asset_uid)
    return [...uids]
}
